/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import PhoneEntryView from '../views/login/password/phoneInputView';
import SmsCodeEntryView from '../views/login/password/smsCodeInputView';
import UserDetailsView from '../views/login/password/userDetailsFormView';
import { ScreenContextProvider } from './screenContext';
import Navbar from '../components/navbar';
import Screen from '../views/screen/screen';
import useSessionStorage from '../hooks/useSessionStorage';
import request from '../utils/request';
import useRequestError from '../hooks/useRequestError';
import useLanguage from '../hooks/useLanguage';
import useTheme from '../hooks/useTheme';
import layout from '../constants/layout';

export interface IUserDetailsForm {
  title: string;
  fields: Array<{
    id: string;
    type: string;
    placeholder: string;
    value: string;
    required: boolean;
    selected: boolean;
  }>;
}

export interface IDeviceRegistrationForm {
  defaultCountryCode: string;
  userDetailsForm: IUserDetailsForm;
}

interface IPasswordLoginContext {
  phone: {
    countryCode: string;
    phoneNumber: string;
  };
  // What is in the code field right now.
  smsCode: string;
  // The code the backend has already accepted. The details form replays it to
  // finish a new signup, so it outlives the field it was typed into.
  verifiedCode: string;
  deviceRegistrationForm?: IDeviceRegistrationForm;
  setPhone: (countryCode: string, phoneNumber: string) => void;
  setSmsCode: (smsCode: string) => void;
  setVerifiedCode: (verifiedCode: string) => void;
}

export interface IPasswordLoginSource {
  viewType?:
    | 'PHONE_ENTRY'
    | 'SMS_CODE_ENTRY'
    | 'PASSWORD_ENTRY'
    | 'USER_DETAILS';
  screenResource?: string;
  // Whether this account is eligible for France's magic-link password reset:
  // a verified email on file, and in the `magix-link-reset` A/B test. Resolved
  // server-side during SMS verification - view-layer is the single source of
  // truth for this decision, so it is used as-is rather than re-derived here
  // (e.g. no separate country check - view-layer already only sets it for FR).
  magicLinkResetEnabled?: boolean;
}

interface ILoginState {
  phone: {
    countryCode: string;
    phoneNumber: string;
  };
  smsCode: string;
  verifiedCode: string;
  deviceRegistrationForm?: IDeviceRegistrationForm;
}

// What a reload or a back navigation restores. `smsCode` is deliberately
// absent: restoring the field would hand the code screen a complete code
// nobody typed, and it would be sent again. `verifiedCode` is safe to keep --
// nothing submits it on sight, and the details form needs it to finish a
// signup the patient reloaded partway through.
type ICachedLoginState = Pick<ILoginState, 'phone' | 'verifiedCode'>;

export const PasswordLoginContext = React.createContext(
  {} as IPasswordLoginContext
);

interface IPasswordLoginContextProvider {
  routeSource?: IPasswordLoginSource;
}

export const PasswordLoginContextProvider = ({
  routeSource,
}: IPasswordLoginContextProvider) => {
  const [getCache, setCache] = useSessionStorage<ICachedLoginState>(
    'PASSWORD_LOGIN_CONTEXT'
  );
  const setCacheRef = useRef(setCache);
  const getCacheRef = useRef(getCache);

  const handleRequestErrorRef = useRef(useRequestError());
  const language = useLanguage();
  const { color, font } = useTheme();

  const [state, setState] = useState<ILoginState>({
    phone: getCacheRef.current()?.phone || {
      countryCode: '',
      phoneNumber: '',
    },
    smsCode: '',
    verifiedCode: getCacheRef.current()?.verifiedCode || '',
  });

  useEffect(() => {
    // This request fetches the current market country calling code
    // and form config used to fill user details later in login flow
    request<IDeviceRegistrationForm>('/api/view/device_registration_form')
      .then((res) => {
        setState((prevState) => {
          // Keep the same `phone` when there is nothing to fill in. A fresh
          // object re-runs every effect watching it, and on the code screen
          // that means re-submitting a code the patient already sent.
          if (prevState.phone.countryCode) {
            return { ...prevState, deviceRegistrationForm: res };
          }

          return {
            ...prevState,
            phone: {
              ...prevState.phone,
              countryCode: res.defaultCountryCode,
            },
            deviceRegistrationForm: res,
          };
        });
      })
      .catch((e) => {
        handleRequestErrorRef.current(e);
      });
  }, []);

  useEffect(() => {
    setCacheRef.current({
      phone: state.phone,
      verifiedCode: state.verifiedCode,
    });
  }, [state.phone, state.verifiedCode]);

  const setPhone = useCallback((countryCode: string, phoneNumber: string) => {
    setState((prevState) => {
      return {
        ...prevState,
        phone: {
          countryCode,
          phoneNumber,
        },
      };
    });
  }, []);

  const setSmsCode = useCallback((smsCode: string) => {
    setState((prevState) => {
      return {
        ...prevState,
        smsCode,
      };
    });
  }, []);

  const setVerifiedCode = useCallback((verifiedCode: string) => {
    setState((prevState) => {
      return {
        ...prevState,
        verifiedCode,
      };
    });
  }, []);

  const getView = () => {
    switch (routeSource?.viewType) {
      case 'SMS_CODE_ENTRY':
        return <SmsCodeEntryView />;
      case 'USER_DETAILS':
        return <UserDetailsView />;
      case 'PASSWORD_ENTRY':
        // we get VL screen url to set and verify password
        return (
          <ScreenContextProvider
            routeSource={{ url: routeSource.screenResource }}
          >
            {/* Navbar */}
            <Navbar />

            <Screen visibleNavbar />

            {routeSource.magicLinkResetEnabled && (
              <div
                css={{
                  position: 'absolute',
                  // This screen renders as a backend-driven Screen we don't
                  // control, so we can't measure its actual content height.
                  // This offset is a best-effort approximation of "right
                  // below the password field" (navbar + title + one input),
                  // matching where the screen's own equivalent button sits
                  // when it's shown instead of this link. Re-tune if that
                  // screen's content changes.
                  top: layout.APP_NAVBAR_HEIGHT + 190,
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                }}
              >
                <Link
                  to={{
                    pathname: '/magic-link',
                    state: {
                      countryCode: state.phone.countryCode,
                      phoneNumber: state.phone.phoneNumber,
                    },
                  }}
                  css={{
                    color: color.PRIMARY,
                    fontFamily: font.FAMILY_BOLD,
                    textDecoration: 'none',
                  }}
                >
                  {(() => {
                    const key =
                      'account_recovery_untrusted_device-forgot-password';
                    const translated = language.get(key);

                    // language.get() returns the raw key on a miss (see
                    // useLanguage.ts). This key currently lives only in the
                    // "Backend" Lokalise file, not "Mobile" (the one patient-web
                    // reads) - fall back to the known FR copy until that's
                    // fixed, rather than showing the key.
                    return translated === key
                      ? 'Mot de passe oublié ?'
                      : translated;
                  })()}
                </Link>
              </div>
            )}
          </ScreenContextProvider>
        );
      // Password login default screen
      case 'PHONE_ENTRY':
      default:
        return <PhoneEntryView />;
    }
  };

  return (
    <PasswordLoginContext.Provider
      value={{
        ...state,
        setPhone,
        setSmsCode,
        setVerifiedCode,
      }}
    >
      {getView()}
    </PasswordLoginContext.Provider>
  );
};
